
import { defineComponent } from 'vue';
import {
    AudioPlayer,
    PlaybackEventType,
    RepeatMode,
    ShuffleMode,
} from './player';
import { artistNames, LibraryManager } from './library';
import SliderBar from './common/SliderBar.vue';
import { formatTime } from './common/utils';
import {
    UNKNOWN_ARTIST_NAME,
    UNKNOWN_ALBUM_NAME,
    UNKNOWN_TRACK_NAME,
} from './common/constants';
import { Settings } from './common/settings';

export default defineComponent({
    components: { SliderBar },
    props: {
        player: {
            type: AudioPlayer,
            required: true,
        },
        settings: {
            type: Settings,
            required: true,
        },
        libraryManager: {
            type: LibraryManager,
            required: true,
        },
    },
    data() {
        return {
            activeTrack: this.player.activeTrack(),
            playing: this.player.playing,
            repeatMode: this.player.repeatMode,
            shuffleMode: this.player.shuffleMode,
            muted: false,
            scrubbing: false,
            duration: this.player.activeTrack()?.duration || 0,
            currentTime: 0,
            scrubberTime: 0,
            volume: this.player.volume * 100,
        };
    },
    watch: {
        volume(newValue) {
            const bounded = Math.min(100, Math.max(newValue, 0));
            this.player.setVolume(bounded / 100);
            this.settings.set('volume', bounded / 100);
            this.muted = this.player.muted;
        },
    },
    emits: ['toggle-play'],
    created() {
        this.player.playbackEvent.on((evt) => {
            switch (evt.type) {
                case PlaybackEventType.Play:
                    this.playing = true;
                    this.activeTrack = evt.track;
                    this.currentTime = 0;
                    this.duration = evt.track.duration;
                    break;
                case PlaybackEventType.Resume:
                    this.playing = true;
                    break;
                case PlaybackEventType.Pause:
                    this.playing = false;
                    break;
                case PlaybackEventType.End:
                    this.playing = false;
                    this.activeTrack = null;
                    this.currentTime = 0;
                    this.duration = 0;
                    break;
                case PlaybackEventType.Time:
                    this.currentTime = evt.time;
                    this.scrubberTime = evt.time;
                    break;
            }
        });
    },
    computed: {
        trackName(): string {
            const title = this.activeTrack && this.activeTrack.name;

            return title || UNKNOWN_TRACK_NAME;
        },
        artistName(): string {
            if (this.activeTrack) {
                return artistNames(this.activeTrack);
            } else {
                return UNKNOWN_ARTIST_NAME;
            }
        },
        albumName(): string {
            const album = this.activeTrack && this.activeTrack.album;
            if (album && album.name) {
                return album.name;
            }
            return UNKNOWN_ALBUM_NAME;
        },
        albumArtSrc(): string | null {
            if (!this.activeTrack) {
                return null;
            }
            return this.libraryManager.getTrackArtUrl(this.activeTrack, 100);
        },
        repeatModeLabel(): string {
            switch (this.repeatMode) {
                case RepeatMode.Off:
                    return 'Repeat: Off';
                case RepeatMode.Repeat:
                    return 'Repeat: All';
                case RepeatMode.RepeatOne:
                    return 'Repeat: One';
                default:
                    return 'Repeat';
            }
        },
    },
    methods: {
        togglePlay() {
            this.player.togglePlay();
        },
        toggleShuffle() {
            this.shuffleMode = this.player.toggleShuffle();
            this.settings.set('shuffleMode', this.shuffleMode);
        },
        toggleRepeat() {
            this.repeatMode = this.player.nextRepeatMode();
            this.settings.set('repeatMode', this.repeatMode);
        },
        toggleMute() {
            this.muted = this.player.toggleMute();
        },
        isShuffling() {
            return this.shuffleMode === ShuffleMode.Shuffle;
        },
        isRepeat() {
            return this.repeatMode !== RepeatMode.Off;
        },
        isRepeatOne() {
            return this.repeatMode === RepeatMode.RepeatOne;
        },
        formatTime(timeValue: number): string {
            return formatTime(timeValue);
        },
        previousTrack() {
            this.player.previousTrack();
        },
        nextTrack() {
            this.player.nextTrack();
        },
        scrubTo(newValue: number) {
            this.scrubberTime = newValue;
            this.player.setTime(newValue);
        },
    },
});
